import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip
} from '@mui/material';
import { useData } from '../../context/DataContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import hospitalImage from '../../assets/homepageimages/hospital-image.webp';
import PageHero from './PageHero';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const About = () => {
  const { state } = useData();
  const { pages, boardMembers } = state;
  
  // Filter active board members
  const activeBoardMembers = boardMembers?.filter(member => member.isActive !== false) || [];

  const stats = [
    { icon: <LocalHospitalIcon />, number: '30+', label: 'Years of Service' },
    { icon: <PeopleIcon />, number: '50+', label: 'Expert Doctors' },
    { icon: <EmojiEventsIcon />, number: '10,000+', label: 'Happy Patients' },
  ];

  const values = [
    'Compassionate care for every patient',
    'Excellence in medical treatment',
    'Innovation in healthcare technology',
    'Community-focused healthcare',
    'Professional development for staff',
    'Patient safety as top priority'
  ];

  return (
    <Box>
      <PageHero
        src={hospitalImage}
        alt="About Hearts International Hospital"
        title="About Hearts International Hospital"
        subtitle="Serving our community with excellence and compassion"
      />

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" gutterBottom>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph>
              Hearts International Hospital has been at the forefront of healthcare innovation for over three decades.
              Founded with a vision to provide exceptional medical care to our community, we have grown 
              from a small local clinic to a comprehensive healthcare facility serving thousands of patients annually.
            </Typography>
            <Typography variant="body1" paragraph>
              Our commitment to excellence has remained unwavering throughout our journey. We continuously 
              invest in the latest medical technology, attract top-tier medical professionals, and maintain 
              the highest standards of patient care and safety.
            </Typography>
            <Typography variant="body1">
              Today, Hearts International Hospital stands as a beacon of hope and healing, offering a wide range of
              medical services with compassion, expertise, and dedication to improving the health and 
              well-being of our community.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={pages.about?.image || hospitalImage}
              alt="Hearts International Hospital"
              sx={{
                width: '100%',
                height: 400,
                objectFit: 'cover',
                borderRadius: 2,
                boxShadow: 3
              }}
            />
          </Grid>
        </Grid>

        {/* Stats Section */}
        <Box sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom textAlign="center" sx={{ mb: 6 }}>
            Our Numbers
          </Typography>
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={4} key={index}>
                <Card sx={{ textAlign: 'center', py: 3 }}>
                  <CardContent>
                    <Box sx={{ color: 'primary.main', mb: 2 }}>
                      {stat.icon}
                    </Box>
                    <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Mission & Values */}
        <Grid container spacing={6} sx={{ py: 8 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" gutterBottom>
              Our Mission
            </Typography>
            <Typography variant="body1" paragraph>
              To provide exceptional healthcare services that improve the quality of life for our patients 
              and their families, while advancing medical knowledge and technology through research and education.
            </Typography>
            <Typography variant="body1">
              We are committed to delivering compassionate, patient-centered care in a safe and healing 
              environment, with respect for the dignity and diversity of all individuals we serve.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" gutterBottom>
              Our Values
            </Typography>
            <List>
              {values.map((value, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={value} />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>

        {/* Board of Directors Section */}
        <Box sx={{ py: 8 }}>
          <Typography variant="h4" component="h2" gutterBottom textAlign="center" sx={{ mb: 6 }}>
            Board of Directors
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ mb: 6, maxWidth: 800, mx: 'auto' }}>
            Our hospital is led by experienced healthcare professionals who are dedicated to maintaining 
            the highest standards of medical care and patient satisfaction.
          </Typography>
          {activeBoardMembers.length > 0 ? (
            <Grid container spacing={4}>
              {activeBoardMembers.map((member) => (
                <Grid item xs={12} sm={6} md={4} key={member.id}>
                  <Card sx={{ 
                    textAlign: 'center', 
                    height: '100%',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 6
                    }
                  }}>
                    <CardContent>
                      <Avatar
                        src={member.image}
                        alt={member.name}
                        sx={{ 
                          width: 120, 
                          height: 120, 
                          mx: 'auto', 
                          mb: 3,
                          boxShadow: 3
                        }}
                      />
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {member.name}
                      </Typography>
                      <Chip 
                        label={member.position} 
                        color="primary" 
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                      {member.bio && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ lineHeight: 1.6 }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(member.bio) }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No board members available at the moment.
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default About; 